import {AfterViewInit, Component, Input, ViewChild} from '@angular/core';
import { AccountModel } from "../../../../../../domain/models/account.model";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatTable, MatTableModule } from "@angular/material/table";
import {
  EnrollmentTypeIndicatorComponent
} from "../../../../../../common/enrollment-type-indicator/enrollment-type-indicator.component";
import { FormsModule } from "@angular/forms";
import { MatChipsModule } from "@angular/material/chips";
import { MatFormFieldModule, MatSuffix } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { CommonModule } from "@angular/common";
import { StatusIndicatorComponent } from "../../../../../../common/status-indicator/status-indicator.component";
import { RouterModule } from "@angular/router";
import { MatSelectModule } from "@angular/material/select";
import { PropertyService } from "../../../../../../domain/services/property/property.service";
import { PropertiesDatasource } from "./properties-datasource";
import {
  FrequencyLabelMap,
  FrequencyStrMap,
  OccupationFrequency
} from "../../../../../../domain/enums/occupation-freq.enum";
import { EnrollmentRentalPropertyModel } from "../../../../../../domain/models/enrollment-rental-property.model";
import { FilterTagContainerComponent } from '../../../../../../common/filter-tag-container/filter-tag-container.component';
import { Icons } from '../../../../../../domain/enums/icons-enums';
import { PropertyStatusEnum } from '../../../../../../domain/enums/property-status.enum';
import {RentalPropertyModel} from "../../../../../../domain/models/rental-property.model";
import {FrequencyPipe} from "../../../../../../common/frequency-pipe/frequency.pipe";
import {RentalPropertyStatusStrMap} from "../../../../../../domain/enums/rental-property-status.enum";
import {DialogService} from "../../../../../../domain/services/dialog/dialog.service";
import {PropertyDialogService} from "../../../../../../domain/services/dialog/property-dialog.service";
import {PropertyDetailComponent} from "../../../../../property/property-detail/property-detail.component";
import {PropertyShareService} from "../../../../../../domain/services/property-share/property-share.service";
import {PropertyMatch, PropertyShare} from "../../../../../../domain/models/property-share.model";
import {DatePipe} from "@angular/common";
import {PropertyMatchCardComponent} from "../../../../../../common/property-match-card/property-match-card.component";

@Component({
  selector: 'app-properties-tab',
  standalone: true,
  imports: [EnrollmentTypeIndicatorComponent, StatusIndicatorComponent, MatTableModule,
    FormsModule, MatIconModule, MatButtonModule, MatInputModule, MatPaginatorModule, MatSortModule,
    MatFormFieldModule, MatSelectModule, MatSuffix, CommonModule, MatChipsModule, RouterModule, StatusIndicatorComponent, FilterTagContainerComponent, FrequencyPipe, PropertyMatchCardComponent],
  templateUrl: './properties-tab.component.html',
  styleUrl: './properties-tab.component.css'
})
export class PropertiesTabComponent implements AfterViewInit {
  @Input() account: AccountModel | undefined;
  @Input() inputProperties: RentalPropertyModel[] | undefined;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(FilterTagContainerComponent) filterContainer!: FilterTagContainerComponent;
  @ViewChild(MatTable) table!: MatTable<EnrollmentRentalPropertyModel>;
  dataSource = new PropertiesDatasource([]);

  displayedColumns = ['id', 'rentalPropertyStreetAddress', 'frequency', 'status', 'sharing'];

  /** Active shares of this account, keyed by this account's property id. */
  sharesByProperty = new Map<number, PropertyShare>();
  /** "Check for duplicates" result for one property at a time. */
  checkedPropertyId: number | null = null;
  matches: PropertyMatch[] = [];
  matchBusy = false;


  properties: RentalPropertyModel[] = [];
  query = "";
  queryStatus: string = "";
  queryOccupation: string = "";
  icons = Icons
  propertyStatusEnum = PropertyStatusEnum
  FILTER_STATUS = "queryStatus";
  FILTER_OCCUPANCY = "queryOccupancy"


  constructor(private readonly service: PropertyService,
              private readonly dialogService: DialogService,
              private readonly propertyDialogService: PropertyDialogService,
              private readonly propertyShareService: PropertyShareService) {
  }

  private loadShares() {
    const accountId = this.account?.id;
    if (!accountId) return;
    this.propertyShareService.forAccount(accountId).subscribe({
      next: shares => {
        this.sharesByProperty = new Map();
        for (const s of shares ?? []) {
          if (!s.active) continue;
          this.sharesByProperty.set(s.homeAccountId === accountId ? s.homePropertyId : s.guestPropertyId, s);
        }
      },
      error: () => this.sharesByProperty = new Map(),
    });
  }

  /** "Shared with X (home|guest) since <handoff>": role is this account's side of the link. */
  shareLabel(share: PropertyShare): string {
    const isHome = share.homeAccountId === this.account?.id;
    const other = isHome ? share.guestAccountName : share.homeAccountName;
    return `Shared with ${other} (${isHome ? 'home' : 'guest'}) since ${new DatePipe('en-US').transform(share.handoffDate)}`;
  }

  unlink(share: PropertyShare, event: Event) {
    event.stopPropagation();
    this.dialogService.openConfirmationDialog('Unlink property',
      `Unlink ${share.address} from ${share.homeAccountName} / ${share.guestAccountName}? Each account files its own returns again.`,
      (ok: boolean) => {
        if (ok !== true) return;
        this.propertyShareService.unlink(share.id).subscribe({
          next: () => this.loadShares(),
          error: err => this.dialogService.openErrorDialog('Unable to unlink', err?.error?.message ?? 'Please try again.'),
        });
      });
  }

  checkDuplicates(propertyId: number, event: Event) {
    event.stopPropagation();
    this.checkedPropertyId = propertyId;
    this.matches = [];
    this.propertyShareService.propertyMatches(propertyId).subscribe({
      next: matches => {
        this.matches = matches ?? [];
        if (!this.matches.length) {
          this.checkedPropertyId = null;
          this.dialogService.openDialog('No duplicates', 'No other account has a property at this address.');
        }
      },
      error: err => {
        this.checkedPropertyId = null;
        this.dialogService.openErrorDialog('Unable to check', err?.error?.message ?? 'Please try again.');
      },
    });
  }

  get isManagementCompany(): boolean {
    return this.account?.type === 'PROPERTY_MANAGEMENT';
  }

  linkMatch(match: PropertyMatch, newIsHome: boolean) {
    const self = this.checkedPropertyId!;
    const [guest, home] = newIsHome ? [match.propertyId, self] : [self, match.propertyId];
    this.matchBusy = true;
    this.propertyShareService.link(guest, home).subscribe({
      next: result => {
        this.matchBusy = false;
        this.closeMatches();
        this.loadShares();
        const dupes = result.duplicatePeriods?.length
          ? ` These periods were already billed on both accounts — amend/credit them: ${result.duplicatePeriods.join(', ')}`
          : '';
        this.dialogService.openDialog('Linked', `Linked with ${match.accountName}. Handoff date: ${new DatePipe('en-US').transform(result.handoffDate)}.${dupes}`);
      },
      error: err => {
        this.matchBusy = false;
        this.dialogService.openErrorDialog('Unable to link', err?.error?.message ?? 'Please try again.');
      },
    });
  }

  dismissMatch(match: PropertyMatch) {
    this.matchBusy = true;
    this.propertyShareService.dismiss(this.checkedPropertyId!, match.propertyId).subscribe({
      next: () => {
        this.matchBusy = false;
        this.matches = this.matches.filter(m => m !== match);
        if (!this.matches.length) this.closeMatches();
      },
      error: err => {
        this.matchBusy = false;
        this.dialogService.openErrorDialog('Unable to dismiss', err?.error?.message ?? 'Please try again.');
      },
    });
  }

  closeMatches() {
    this.checkedPropertyId = null;
    this.matches = [];
  }

  private init(){
    let accountId = this.account?.id?? null;
    let property = this.account?.rentalProperty?? null;
    if(this.inputProperties){
      this.properties = this.inputProperties;
      this.rebuildDataSource(this.applyAllFilters(this.properties));
    }
    else if (accountId) {
      // account.rentalProperty is only the account's first property; always load the full list.
      this.service.getByAccount(accountId).subscribe(value => {
        this.properties = value?.length ? value : (property ? [property] : []);
        this.rebuildDataSource(this.applyAllFilters(this.properties));
      }, () => {
        this.properties = property ? [property] : [];
        this.rebuildDataSource(this.applyAllFilters(this.properties));
      });
    }
    else if(property){
      this.properties = [property];
      this.rebuildDataSource(this.applyAllFilters(this.properties));
    }
  }

  ngAfterViewInit(): void {
    this.init();
    this.loadShares();
  }

  rebuildDataSource(data: RentalPropertyModel[]) {
    this.dataSource = new PropertiesDatasource(data);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;
  }

  onFilter() {
    this.rebuildDataSource(this.applyAllFilters(this.properties));
  }

  applyFrequencyFilter(data: RentalPropertyModel[]) {
    if (!this.queryOccupation) {
      this.filterContainer.removeTag({ value: this.queryOccupation, type: this.FILTER_OCCUPANCY,
        label: this.queryOccupation })
    } else {
      let freq = FrequencyStrMap.get(this.queryOccupation);
      let freqLabel = freq ? FrequencyLabelMap.get(freq) : this.queryOccupation
      this.filterContainer.addFilterTag({ value: this.queryOccupation, type: this.FILTER_OCCUPANCY,
        label: freqLabel?? this.queryOccupation })
    }


    if (data && data.length > 0 && this.queryOccupation
      && this.queryOccupation.length > 0) {
      return data.filter(value => value.frequency === this.queryOccupation);
    } else {
      return data;
    }
  }

  applyStatusFilter(data: RentalPropertyModel[]) {
    if (!this.queryStatus) {
      this.filterContainer.removeTag({ value: this.queryStatus, type: this.FILTER_STATUS,
        label: this.queryStatus })
    } else {
      this.filterContainer.addFilterTag({ value: this.queryStatus, type: this.FILTER_STATUS,
        label: this.queryStatus, icon: this.queryStatus.toLocaleLowerCase() + '-icon' })
    }
    if (data && data.length > 0 && this.queryStatus
      && this.queryStatus.length > 0) {
      return data.filter(value => value.status === this.queryStatus);
    } else {
      return data;
    }
  }

  removeFilter(event: { value: string, type: string }) {
    if (event.type == this.FILTER_STATUS) {
      this.queryStatus = ""
      this.filterContainer.removeTag({ value: this.queryStatus, type: this.FILTER_STATUS, label: this.queryStatus })
    } else {
      this.queryOccupation = "";
      this.filterContainer.removeTag({ value: this.queryOccupation, type: this.FILTER_OCCUPANCY, label: this.queryOccupation })
    }
  }


  applySearchFilter(data: RentalPropertyModel[]) {
    if (data && data.length > 0) {
      if (this.query.length > 2) {
        return data.filter(value =>
          value.status?.toLowerCase().includes(this.query.toLowerCase())
          || value.rentalPropertyStreetAddress?.toLowerCase().includes(this.query.toLowerCase())
          || value.frequency?.toLowerCase().includes(this.query.toLowerCase()));
      }
    }
    return data;
  }

  clearSerch() {
    this.query = '';
    this.onFilter();
  }

  removeFilters() {
    this.query = "";
    this.queryStatus = "";
    this.queryOccupation = "";
    this.onFilter();
  }

  areActiveFilters() {
    return (this.queryStatus) ||
      (this.queryOccupation)
  }

  private applyAllFilters(data: RentalPropertyModel[]) {
    this.paginator.pageIndex = 0;
    return this.applySearchFilter(
      this.applyStatusFilter(
        this.applyFrequencyFilter(data)));
  }

  protected readonly OccupationFrequency = OccupationFrequency;
  protected readonly FrequencyLabelMap = FrequencyLabelMap;
  protected readonly RentalPropertyStatusStrMap = RentalPropertyStatusStrMap;

  openAddPropertyDialog() {
    if (!this.account) return;
    this.propertyDialogService.openPropertyDialog(this.account, () => this.reloadProperties());
  }

  private reloadProperties() {
    const accountId = this.account?.id;
    if (!accountId) return;
    this.service.getByAccount(accountId).subscribe({
      next: (value) => this.rebuildDataSource(this.applyAllFilters(this.properties = value ?? [])),
      error: () => this.rebuildDataSource([]),
    });
  }
}
